import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { Question, Client, Setting } from '../../types';
import QuestionForm from './QuestionForm';
import './QuestionList.css';

interface QuestionListProps {
  clientId?: number | null;
}

const QuestionList: React.FC<QuestionListProps> = ({ clientId }) => {
  const { user, isAdmin } = useAuth();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(clientId || null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [questionLimit, setQuestionLimit] = useState(5);
  const [reordering, setReordering] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    if (selectedClientId || (!isAdmin() && user?.clients?.length)) {
      fetchQuestions();
    } else if (isAdmin()) {
      setLoading(false);
      setQuestions([]);
    }
  }, [selectedClientId, user?.clients]);

  const fetchClients = async () => {
    try {
      const response = await api.get<Client[]>('/clients');
      setClients(response.data);

      if (!isAdmin() && response.data.length > 0 && !selectedClientId) {
        setSelectedClientId(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (selectedClientId) {
        params.client_id = selectedClientId;
      }

      const [questionsResponse, settingsResponse] = await Promise.all([
        api.get<Question[]>('/questions', { params }),
        selectedClientId
          ? api.get<Setting[]>(`/settings?client_id=${selectedClientId}`)
          : Promise.resolve({ data: [] as Setting[] }),
      ]);

      const sorted = [...questionsResponse.data].sort((a, b) => {
        const orderA = a.sort_order ?? 0;
        const orderB = b.sort_order ?? 0;
        if (orderA !== orderB) return orderA - orderB;
        return a.id - b.id;
      });
      setQuestions(sorted);

      const limitSetting = settingsResponse.data.find((s) => s.key === 'question_count');
      const parsed = parseInt(String(limitSetting?.value ?? '5'), 10);
      setQuestionLimit(!Number.isNaN(parsed) && parsed > 0 ? parsed : 5);
    } catch (error) {
      console.error('Failed to fetch questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (question: Question) => {
    try {
      await api.patch(`/questions/${question.id}/status`, {
        status: !question.status,
      });
      fetchQuestions();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to update question status');
    }
  };

  const handleDelete = async (question: Question) => {
    if (!window.confirm('Are you sure you want to delete this question? This action cannot be undone if the question has reviews.')) {
      return;
    }

    try {
      await api.delete(`/questions/${question.id}`);
      fetchQuestions();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to delete question');
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingQuestion(null);
    fetchQuestions();
  };

  const handleEdit = (question: Question) => {
    setEditingQuestion(question);
    setShowForm(true);
  };

  const persistOrder = async (nextQuestions: Question[]) => {
    if (!selectedClientId) return;
    const previous = questions;
    setQuestions(nextQuestions);
    setReordering(true);
    try {
      const response = await api.post<Question[]>('/questions/reorder', {
        client_id: selectedClientId,
        order: nextQuestions.map((q) => q.id),
      });
      setQuestions(response.data);
    } catch (error: any) {
      setQuestions(previous);
      alert(error.response?.data?.message || 'Failed to update question order');
    } finally {
      setReordering(false);
    }
  };

  const moveQuestion = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= questions.length) return;
    const next = [...questions];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    persistOrder(next);
  };

  const filteredQuestions = questions.filter((q) =>
    q.question_title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const canReorder = Boolean(selectedClientId) && !searchTerm.trim();
  const atQuestionLimit = Boolean(selectedClientId) && questions.length >= questionLimit;

  const getSelectedClientName = (): string => {
    if (!selectedClientId) return '';
    const client = clients.find((c) => c.id === selectedClientId);
    return client?.client_name || '';
  };

  return (
    <div className="questions-list">
      <div className="questions-header">
        <h2>Questions</h2>
        {getSelectedClientName() && (
          <div className="company-name-header">
            <span>Company: {getSelectedClientName()}</span>
          </div>
        )}
      </div>

      {isAdmin() && (
        <div className="client-selector-section">
          <label htmlFor="client-select">Select Company:</label>
          <select
            id="client-select"
            value={selectedClientId || ''}
            onChange={(e) => setSelectedClientId(e.target.value ? parseInt(e.target.value, 10) : null)}
            className="client-select"
          >
            <option value="">All Companies</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.client_name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="questions-actions">
        <div className="search-filter">
          <input
            type="text"
            placeholder="Search questions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        {selectedClientId && (
          <div className="questions-actions-right">
            <span className="question-count-meta">
              {questions.length} / {questionLimit} questions
            </span>
            {!atQuestionLimit ? (
              <button
                onClick={() => {
                  setEditingQuestion(null);
                  setShowForm(true);
                }}
                className="btn btn-primary"
              >
                Add New Question
              </button>
            ) : (
              <span className="question-limit-reached">
                {isAdmin()
                  ? 'Question limit reached. Increase Question Count in Company Settings to add more.'
                  : 'Question limit reached. Contact an administrator to increase the limit.'}
              </span>
            )}
          </div>
        )}
      </div>

      {showForm && selectedClientId && (editingQuestion || !atQuestionLimit) && (
        <QuestionForm
          question={editingQuestion}
          clientId={selectedClientId}
          onSuccess={handleFormSuccess}
          onCancel={() => {
            setShowForm(false);
            setEditingQuestion(null);
          }}
        />
      )}

      {loading ? (
        <div className="loading">Loading questions...</div>
      ) : filteredQuestions.length === 0 ? (
        <div className="no-data">
          {selectedClientId ? 'No questions found' : 'Please select a company to view questions'}
        </div>
      ) : (
        <table className="questions-table">
          <thead>
            <tr>
              {canReorder && <th>Order</th>}
              <th>ID</th>
              <th>Question</th>
              {isAdmin() && <th>Company</th>}
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredQuestions.map((question) => {
              const index = questions.findIndex((q) => q.id === question.id);
              return (
                <tr key={question.id}>
                  {canReorder && (
                    <td>
                      <div className="order-buttons">
                        <button
                          type="button"
                          className="btn btn-sm btn-order"
                          onClick={() => moveQuestion(index, -1)}
                          disabled={reordering || index <= 0}
                          title="Move up"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-order"
                          onClick={() => moveQuestion(index, 1)}
                          disabled={reordering || index >= questions.length - 1}
                          title="Move down"
                        >
                          ↓
                        </button>
                      </div>
                    </td>
                  )}
                  <td>{question.id}</td>
                  <td className="question-title">{question.question_title}</td>
                  {isAdmin() && (
                    <td>
                      {question.client ? (
                        <span className="client-name">{question.client.client_name}</span>
                      ) : (
                        <span className="no-client">—</span>
                      )}
                    </td>
                  )}
                  <td>
                    <span className={`status-badge ${question.status ? 'active' : 'inactive'}`}>
                      {question.status ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => handleToggleStatus(question)}
                        className={`btn btn-sm ${question.status ? 'btn-warning' : 'btn-success'}`}
                        title={question.status ? 'Deactivate' : 'Activate'}
                      >
                        {question.status ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => handleEdit(question)}
                        className="btn btn-sm btn-primary"
                        title="Edit"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(question)}
                        className="btn btn-sm btn-danger"
                        title="Delete"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default QuestionList;
